import React, { useCallback } from 'react';
import {
  defineMessages,
  FormattedMessage, useIntl,
} from 'react-intl';
import { FormikProps } from 'formik';

import {
  Heading, Alert, Grid, TextField, DatepickerField, SelectField, Button, RadioGroup,
} from '@printdeal/ui-library';
import type { ValidationVariant } from '@printdeal/ui-library';

import { messages } from '../../translations/account';
import { FormValues } from '../../../product/selector/components/InventoryCheck/InventoryCheckQuoteForm';
import useFormikValidation from '../../../../hooks/useFormikValidation';

const errorMessages = defineMessages({
  emptyRequiredField: {
    id: 'account.details.error.required.inputField',
    defaultMessage: 'The required field cannot be empty',
  },
  invalidEmail: {
    id: 'account.details.error.invalid.email',
    defaultMessage: 'Please enter a valid email address',
  },
});

export interface FeedbackValues {
  kind: ValidationVariant,
  message: string,
}

export interface AccountFormValues extends FormValues {
  dateOfBirth?: string;
  locale: string;
  company: string;
  cocNumber: string;
  vatId: string;
  isCompany: string;
  value: string;
  newsletterOptIn: boolean;
}

interface AccountFormProps {
  formik: FormikProps<AccountFormValues>,
  feedback?: FeedbackValues,
  locales: string[],
  allowEmptyCompany: boolean,
}

type VatIdError = 'validation.vatId.error.regexMismatch' | 'validation.vatId.error.invalidVatId' | 'validation.vatId.error.serviceUnavailable';

const AccountForm = ({
  formik: {
    handleSubmit, errors, touched, getFieldProps, isSubmitting,
    isValid, handleChange, values, setFieldValue, handleBlur,
  },
  feedback,
  locales,
  allowEmptyCompany,
}: AccountFormProps) => {
  const intl = useIntl();
  const { validation } = useFormikValidation({ values, errors, touched });

  const getErrorMessage = (fieldName: keyof AccountFormValues) => {
    if (validation[fieldName] === 'error') {
      return fieldName === 'email'
        ? intl.formatMessage(errorMessages.invalidEmail)
        : intl.formatMessage(errorMessages.emptyRequiredField);
    }
    return null;
  };

  const handleVatIdChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('vatId', event.target.value.replace(/\W/g, ''));
  }, [setFieldValue]);

  return (
    <form method="post" onSubmit={handleSubmit} autoComplete="off" noValidate className="data-sentry-block pd-max-w-2xl">
      <fieldset
        name="userDetails"
        disabled={isSubmitting}
      >
        <Heading as="legend" size="2" className="pd-mb-6">
          <FormattedMessage id="account.details.personal.edit" defaultMessage="Edit information" />
        </Heading>
        {feedback && (
        <Alert
          variant={feedback.kind}
          isDismissible
          dismissLabel={intl.formatMessage({
            id: 'general.alert.dismiss',
            defaultMessage: 'Dismiss',
          })}
          className="pd-my-5"
        >
          {feedback.message}
        </Alert>
        )}
        <Grid className="pd-mb-12 pd-gap-6 sm:pd-grid-cols-2">
          <Grid.Item>
            <TextField
              id="firstName"
              validation={validation.firstName}
              validationMessage={getErrorMessage('firstName')}
              required
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              label={intl.formatMessage({
                id: 'account.details.label.name',
                defaultMessage: 'Name',
              })}
              {...getFieldProps('firstName')}
            />
          </Grid.Item>
          <Grid.Item>
            <TextField
              id="lastName"
              validation={validation.lastName}
              validationMessage={getErrorMessage('lastName')}
              required
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              label={intl.formatMessage({
                id: 'account.details.label.lastName',
                defaultMessage: 'Last name',
              })}
              {...getFieldProps('lastName')}
            />
          </Grid.Item>
          <Grid.Item>
            <TextField
              id="email"
              validation={validation.email}
              validationMessage={getErrorMessage('email')}
              required
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              label={intl.formatMessage({
                id: 'account.details.label.email',
                defaultMessage: 'Email address',
              })}
              {...getFieldProps('email')}
            />
          </Grid.Item>
          <Grid.Item>
            <DatepickerField
              id="dateOfBirth"
              variant="birthday"
              validation={validation.dateOfBirth}
              datepickerScreenReaderLabel={intl.formatMessage({
                id: 'general.form.datePickerLabel',
                defaultMessage: 'Choose your date of birth',
              })}
              label={intl.formatMessage({
                id: 'account.details.label.dateOfBirth',
                defaultMessage: 'Date of birth',
              })}
              {...getFieldProps('dateOfBirth')}
            />
          </Grid.Item>
          {locales.length > 1 && (
          <Grid.Item>
            <SelectField
              id="locale"
              validation={validation.locale}
              required
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              label={intl.formatMessage({
                id: 'account.details.label.language',
                defaultMessage: 'Preferred language',
              })}
              description={intl.formatMessage({
                id: 'account.details.label.languageMessage',
                defaultMessage: 'The language will be used for emails.',
              })}
              {...getFieldProps('locale')}
            >
              {locales.map((locale) => (
                <SelectField.Option key={locale} value={locale.substring(0, 2)}>
                  {intl.formatMessage(messages[locale.substring(0, 2) as 'nl' | 'fr'])}
                </SelectField.Option>
              ))}
            </SelectField>
          </Grid.Item>
          )}
        </Grid>
      </fieldset>

      <RadioGroup
        id="isCompany"
        disabled={isSubmitting}
        required
        requiredLabel={intl.formatMessage({
          id: 'general.form.requiredLabel',
          defaultMessage: 'Required field',
        })}
        label={intl.formatMessage({
          id: 'account.details.label.accountType',
          defaultMessage: 'Type account',
        })}
        className="pd-mb-6 pd-w-48"
        {...getFieldProps('isCompany')}
      >
        <RadioGroup.Field
          id="business"
          value="true"
          checked={values.isCompany === 'true'}
          label={intl.formatMessage({
            id: 'account.details.form.label.accounttype.business',
            defaultMessage: 'Business',
          })}
          onChange={handleChange}
          required
        />
        <RadioGroup.Field
          id="private"
          value="false"
          checked={values.isCompany === 'false'}
          label={intl.formatMessage({
            id: 'account.details.form.label.accounttype.private',
            defaultMessage: 'Private',
          })}
          onChange={handleChange}
          required
        />
      </RadioGroup>

      {values.isCompany === 'true' && (
      <fieldset
        name="companyDetails"
        disabled={isSubmitting}
      >
        <legend className="pd-sr-only">
          <FormattedMessage id="account.details.label.companyDetails" defaultMessage="Company details" />
        </legend>
        <Grid className="pd-mb-6 pd-gap-6 sm:pd-grid-cols-2">
          <Grid.Item className="sm:pd-col-span-2">
            <TextField
              id="company"
              validation={validation.company}
              validationMessage={getErrorMessage('company')}
              required={!allowEmptyCompany}
              requiredLabel={intl.formatMessage({
                id: 'general.form.requiredLabel',
                defaultMessage: 'Required field',
              })}
              label={intl.formatMessage({
                id: 'account.details.label.companyName',
                defaultMessage: 'Company name',
              })}
              {...getFieldProps('company')}
            />
          </Grid.Item>
          <Grid.Item>
            <TextField
              id="cocNumber"
              validation={validation.cocNumber}
              label={intl.formatMessage({
                id: 'account.details.label.cocNumber',
                defaultMessage: 'Coc number',
              })}
              description={intl.formatMessage({
                id: 'account.details.helptext.kvkNumber',
                defaultMessage: 'E.g. 08119373',
              })}
              {...getFieldProps('cocNumber')}
            />
          </Grid.Item>
          <Grid.Item>
            <TextField
              id="vatId"
              name="vatId"
              value={values.vatId}
              validation={validation.vatId}
              validationMessage={errors.vatId && intl.formatMessage(messages[errors.vatId as VatIdError])}
              label={intl.formatMessage({
                id: 'account.details.label.vatId',
                defaultMessage: 'VAT number',
              })}
              description={intl.formatMessage({
                id: 'account.details.helptext.vatId',
                defaultMessage: 'E.g. NL8195.55.514.B01',
              })}
              onChange={handleVatIdChange}
              onBlur={handleBlur}
            />
          </Grid.Item>
        </Grid>
      </fieldset>
      )}
      <div className="pd-flex pd-justify-end">
        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={!isValid || isSubmitting}
          data-cy="changeAccountDetailsSubmitButton"
        >
          <FormattedMessage id="account.details.submit.button" defaultMessage="Submit" />
        </Button>
      </div>
    </form>
  );
};

export default AccountForm;
